import { Action } from 'redux';
import { initializeState } from 'src/utils/Initial';
import { isType } from 'typescript-fsa';
import * as Actions from '../actions/search';

/**
 * 検索画面のState型
 */
export interface ISearchState {
	readonly setCategoryAt?: Date;
	readonly categories: ISearchCategoryState;
	readonly details: ISearchDetailState;
	readonly detailsForApply: ISearchDetailState;
	readonly career: ICareerState;
	readonly curriculum: ICurriculumState;
	readonly curriculumAchieveCondition: ICurriculumAchieveConditionState;
	readonly course: ICourseState;
	readonly careerSearchCondition: Actions.ICareerSearchCondition;
	readonly careerSearchConditionDispInfo: Actions.ICareerSearchConditionDispInfo;
	readonly careerCategoryMsts: Actions.ICategoryMst[];
	readonly curriculumCategoryMsts: Actions.ICategoryMst[];
	readonly isOpenApprovalTargetCheck: boolean;
	readonly isOpenApprovals: boolean;
	readonly approvals: IApprovalsState;
	readonly approvalResult: Actions.IApprovalResult;
	readonly isOpenApprovalResult: boolean;
	readonly canApproval: Actions.IApprovalResultErrorMessage;
	readonly isOpenCanApproval: boolean;
	readonly isOpenApprovalConfirm: boolean;
	readonly isOpenSearchDetailCondCategory: boolean;
	readonly OpenCourseDetailDialog: boolean;
	readonly OpenCurriculumDetailDialog: boolean;
	readonly OpenClassStayMealDialog: boolean;
	readonly stayMealDateList: Actions.IStayMealDateList[];
	readonly approvalStayMealList: Actions.IApprovalStayMealData[];
	readonly useFacilityFlg: string;
	readonly stayMealComment: string;
	readonly setCommentFlg: boolean;
	readonly cmFlg: string;
	readonly skillCategoryMsts: Actions.ICategoryMst[];
	readonly skillCodeList: any[];
}

/**
 * 研修情報のState型
 */
export interface ICareerState {
	readonly careerInfo: Actions.ICareerInfo;
	readonly careerClasses: Actions.ICareerClass[];
}

/**
 * カリキュラム情報のState型
 */
export interface ICurriculumState {
	readonly curriculumInfo: Actions.ICurriculumInfo;
	readonly showHRAttribute: string;
}

/**
 * 章立て情報のState型
 */
export interface ICourseState {
	readonly courseInfo: Actions.ICourseInfo;
	readonly courseTopics: Actions.ICourseTopicItem[];
}

/**
 * カリキュラム達成条件のState型
 */
export interface ICurriculumAchieveConditionState {
	readonly curriculumInfo: Actions.ICurriculumInfo;
	readonly requiredCareers: Actions.ISubCareerInfo[];
	readonly optionalCareers: Actions.ISubCareerInfo[];
}

/**
 * カテゴリ検索のState型
 */
export interface ISearchCategoryState {
	data: Actions.ISearchCategoryItem[];
	selectedCareers: Actions.ISelectedCareer[];
}

/**
 * 詳細検索のState型
 */
export interface ISearchDetailState {
	applyFlg: number;
	data: Actions.ISearchDetailItem[];
	selectedCareers: Actions.ISelectedCareer[];
	selectedColumns: number[];
}

/**
 * 受講申請のState型
 */
export interface IApprovalsState {
	data: Actions.IApprovalItem[];
	selectedColumns: number[];
	selectedColumnsForStayMeal: number[];
}

/**
 * 検索画面のState初期値
 */
const initialState: ISearchState = {
	approvalResult: {
		errorMessageList: [],
		exceptionEndFlg: 0,
		success: true,
	},
	approvalStayMealList: [],
	approvals: {
		data: [],
		selectedColumns: [2, 3, 4, 5, 8, 9, 12, 13],
		selectedColumnsForStayMeal: [2, 3, 4, 5, 8, 10, 12, 13, 14],
	},
	canApproval: {
		messageId: '',
		replacementStringList: [],
	},
	career: {
		careerClasses: [],
		careerInfo: {},
	},
	careerCategoryMsts: [],
	careerSearchCondition: {
		assessment: false,
		careerIDs: [],
		careerType: '0',
		categoryId: '',
		categoryName: '',
		detail: [
			{
				__multiselectValues: [],
				__type: '',
				items: [],
				text: '',
				type: '',
			},
		],
		elearning: true,
		endDate: '',
		freeword: '',
		jobCategory: false,
		lowerFlg: false,
		office: false,
		onlyWanted: false,
		position: false,
		qualification: false,
		shugo: true,
		skillTypeList: [],
		sonota: false,
		startDate: '',
		tuusin: true,
		workExperience: false,
	},
	careerSearchConditionDispInfo: {
		generalList: [],
	},
	categories: {
		data: [],
		selectedCareers: [],
	},
	cmFlg: 'N',
	course: {
		courseInfo: {},
		courseTopics: [],
	},
	curriculum: {
		curriculumInfo: {},
		showHRAttribute: '',
	},
	curriculumAchieveCondition: {
		curriculumInfo: {},
		optionalCareers: [],
		requiredCareers: [],
	},
	curriculumCategoryMsts: [],
	details: {
		applyFlg: 0,
		data: [],
		selectedCareers: [],
		selectedColumns: [0, 2, 3, 4, 5, 6, 7, 9],
	},
	detailsForApply: {
		applyFlg: 0,
		data: [],
		selectedCareers: [],
		selectedColumns: [0, 2, 3, 4, 5, 6, 7, 9],
	},
	isOpenApprovalConfirm: false,
	isOpenApprovalResult: false,
	isOpenApprovalTargetCheck: false,
	isOpenApprovals: false,
	isOpenCanApproval: false,
	isOpenSearchDetailCondCategory: false,

	OpenClassStayMealDialog: false,
	OpenCourseDetailDialog: false,
	OpenCurriculumDetailDialog: false,
	setCommentFlg: false,
	skillCategoryMsts: [],
	skillCodeList: [],
	stayMealComment: '',
	stayMealDateList: [],
	useFacilityFlg: 'N',
};

/**
 * 検索画面用reducer
 * @param state State
 * @param action アクション
 */
const search = (state: ISearchState, action: Action) => {
	if (!state) {
		// State初期化処理
		state = initialState;
		state = initializeState(state);
		state.careerSearchCondition.detail = [
			{
				__multiselectValues: [],
				__type: '',
				items: [],
				text: '',
				type: '',
			},
		];
	}
	// カテゴリ検索結果設定
	if (isType(action, Actions.setSearchCategories)) {
		return {
			...state,
			categories: {
				...state.categories,
				data: action.payload.categories,
			},
			setCategoryAt: new Date(),
		};
	}

	// 研修情報設定
	if (isType(action, Actions.setCareerDetail)) {
		return {
			...state,
			career: {
				careerClasses: action.payload.careerClasses,
				careerInfo: action.payload.careerInfo,
			},
		};
	}

	// カリキュラム情報設定
	if (isType(action, Actions.setCurriculumDetail)) {
		return {
			...state,
			curriculum: {
				curriculumInfo: action.payload.curriculumInfo,
				showHRAttribute: action.payload.showHRAttribute,
			},
		};
	}

	// 章立て情報設定
	if (isType(action, Actions.setCourseDetail)) {
		return {
			...state,
			course: {
				courseInfo: action.payload.courseInfo,
				courseTopics: action.payload.courseTopics,
			},
		};
	}

	// カリキュラム達成条件情報設定
	if (isType(action, Actions.setCurriculumAchieveCondition)) {
		return {
			...state,
			curriculumAchieveCondition: {
				curriculumInfo: action.payload.curriculumInfo,
				optionalCareers: action.payload.optionalCareers,
				requiredCareers: action.payload.requiredCareers,
			},
		};
	}

	// 検索条件変更
	if (isType(action, Actions.setCareerSearchCondition)) {
		return {
			...state,
			careerSearchCondition: action.payload.value,
		};
	}

	// 詳細検索条件表示情報設定
	if (isType(action, Actions.setCareerSearchConditionDetail)) {
		return {
			...state,
			careerSearchConditionDispInfo: action.payload.data,
		};
	}

	// 詳細検索結果設定
	if (isType(action, Actions.setSearchDetails)) {
		return {
			...state,
			details: {
				...state.details,
				applyFlg: action.payload.applyFlg,
				data: action.payload.data,
			},
		};
	}

	// 申請用詳細検索結果設定
	if (isType(action, Actions.setSearchDetailsForApply)) {
		return {
			...state,
			detailsForApply: {
				...state.detailsForApply,
				applyFlg: action.payload.applyFlg,
				data: action.payload.data,
			},
		};
	}

	// 詳細検索表示カラム設定
	if (isType(action, Actions.setSearchDetailSelectedColumns)) {
		return {
			...state,
			details: {
				...state.details,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	// 詳細検索条件 分類選択表示設定
	if (isType(action, Actions.openSearchCondCategorySelect)) {
		return {
			...state,
			isOpenSearchDetailCondCategory: action.payload.open,
		};
	}

	// 詳細検索（申請用）表示カラム設定
	if (isType(action, Actions.setSearchDetailForApplySelectedColumns)) {
		return {
			...state,
			detailsForApply: {
				...state.detailsForApply,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	// カテゴリマスタ設定
	if (isType(action, Actions.setCategoryMst)) {
		return {
			...state,
			careerCategoryMsts: action.payload.careerCategories,
			curriculumCategoryMsts: action.payload.curriculumCategories,
			skillCategoryMsts: action.payload.skillCategories,
		};
	}

	// キャリア選択(カテゴリ検索)
	if (isType(action, Actions.setCategorySelectedCareers)) {
		return {
			...state,
			categories: {
				...state.categories,
				selectedCareers: action.payload.selectedCareers,
			},
		};
	}

	// キャリア選択(詳細検索)
	if (isType(action, Actions.setDetailSelectedCareers)) {
		return {
			...state,
			details: {
				...state.details,
				selectedCareers: action.payload.selectedCareers,
			},
		};
	}

	// キャリア選択(申請一覧検索)
	if (isType(action, Actions.setApplicationSelectedCareers)) {
		return {
			...state,
			detailsForApply: {
				...state.detailsForApply,
				selectedCareers: action.payload.selectedCareers,
			},
		};
	}

	// 申請対象確認画面
	if (isType(action, Actions.openApprovalTargetCheck)) {
		return {
			...state,
			isOpenApprovalTargetCheck: action.payload.isOpenApprovalTargetCheck,
		};
	}

	// 申請画面
	if (isType(action, Actions.openApprovals)) {
		return {
			...state,
			isOpenApprovals: action.payload.isOpenApprovals,
		};
	}

	// 申請情報設定
	if (isType(action, Actions.setApprovals)) {
		return {
			...state,
			approvals: {
				...state.approvals,
				data: action.payload.data,
			},
		};
	}

	// 申請表示カラム設定
	if (isType(action, Actions.setApprovalsSelectedColumns)) {
		return {
			...state,
			approvals: {
				...state.approvals,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	// 申請表示カラム設定(施設管理機能フラグが有効の場合)
	if (isType(action, Actions.setApprovalsSelectedColumnsForStayMeal)) {
		return {
			...state,
			approvals: {
				...state.approvals,
				selectedColumnsForStayMeal: action.payload.selectedColumns,
			},
		};
	}

	// 申請結果設定
	if (isType(action, Actions.setApprovalResult)) {
		return {
			...state,
			approvalResult: action.payload.data,
			isOpenApprovalResult: action.payload.isOpenApprovalResult,
		};
	}

	// 受講申請可否設定
	if (isType(action, Actions.setCanApproval)) {
		return {
			...state,
			canApproval: action.payload.data,
			isOpenCanApproval: action.payload.isOpenCanApproval,
		};
	}

	// 受講申請確認ダイアログ表示設定
	if (isType(action, Actions.openApprovalConfirm)) {
		return {
			...state,
			isOpenApprovalConfirm: action.payload.open,
		};
	}

	if (isType(action, Actions.setCourseDetailDialog)) {
		return {
			...state,
			OpenCourseDetailDialog: action.payload.OpenCourseDetailDialog,
		};
	}

	if (isType(action, Actions.setCurriculumDetailDialog)) {
		return {
			...state,
			OpenCurriculumDetailDialog:
				action.payload.OpenCurriculumDetailDialog,
		};
	}

	// 宿泊・食事申込画面
	if (isType(action, Actions.setClassStayMealDialog)) {
		return {
			...state,
			OpenClassStayMealDialog: action.payload.OpenClassStayMealDialog,
		};
	}

	// 宿泊・食事実施日を設定
	if (isType(action, Actions.setStayMealDateList)) {
		return {
			...state,
			stayMealDateList: action.payload.dateList,
		};
	}

	// 宿泊・食事申込情報を設定
	if (isType(action, Actions.setApprovalStayMealList)) {
		if (action.payload.approvalStayMealList.length > 0) {
			return {
				...state,
				approvalStayMealList: action.payload.approvalStayMealList,
			};
		} else {
			return {
				...state,
				approvalStayMealList: action.payload.approvalStayMealList,
				setCommentFlg: false,
				stayMealComment: '',
			};
		}
	}

	// 施設管理オプションを設定
	if (isType(action, Actions.setUseFacilityFlg)) {
		return {
			...state,
			useFacilityFlg: action.payload.useFacilityFlg,
		};
	}

	// 宿泊・食事備考を設定
	if (isType(action, Actions.setStayMealCommment)) {
		return {
			...state,
			setCommentFlg: true,
			stayMealComment: action.payload.comment,
		};
	}

	// 「検索」画面情報(CM利用フラグ)を取得する。
	if (isType(action, Actions.setSearchInfo)) {
		return {
			...state,
			cmFlg: action.payload.cmFlg,
			skillCodeList: action.payload.skillCodeList,
		};
	}

	return state;
};

export default search;



// WEBPACK FOOTER //
// ./src/reducers/search.ts